import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { GenerateContractDialog } from "../components/GenerateContractDialog";
import { ProrogaLetterDialog } from "../components/ProrogaLetterDialog";
import { FileSignature, Loader2, Mail, RefreshCw, ScrollText, Search } from "lucide-react";

const fmtIt = (iso) => {
    if (!iso || !iso.includes("-")) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const TIPO_LABEL = {
    assunzione: "Assunzione",
    proroga: "Proroga",
    trasformazione: "Trasformazione",
};

const STATUS_META = {
    missing: { label: "Mancante", cls: "border-red-200 bg-red-50 text-red-700" },
    outdated: { label: "Da aggiornare", cls: "border-amber-200 bg-amber-50 text-amber-700" },
    ok: { label: "Presente", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
};

export default function ContractsPage() {
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState({ missing: 0, outdated: 0, ok: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("todo"); // todo | missing | outdated | ok | all
    const [search, setSearch] = useState("");
    const [genEmployee, setGenEmployee] = useState(null);
    const [letterTarget, setLetterTarget] = useState(null); // { employee, kind }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/contract-templates/contracts-status");
            setItems(data.items || []);
            setTotals(data.totals || {});
        } catch (e) {
            console.error("Errore caricamento stato contratti:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        let list = items;
        if (filter === "todo") list = list.filter((i) => i.status !== "ok");
        else if (filter !== "all") list = list.filter((i) => i.status === filter);
        const q = search.trim().toLowerCase();
        if (q) {
            list = list.filter(({ employee: e }) =>
                `${e.cognome} ${e.nome} ${e.matricola || ""}`.toLowerCase().includes(q));
        }
        return list;
    }, [items, filter, search]);

    const tabs = [
        { key: "todo", label: `Da fare (${(totals.missing || 0) + (totals.outdated || 0)})` },
        { key: "missing", label: `Mancanti (${totals.missing || 0})` },
        { key: "outdated", label: `Da aggiornare (${totals.outdated || 0})` },
        { key: "ok", label: `Presenti (${totals.ok || 0})` },
        { key: "all", label: `Tutti (${totals.total || 0})` },
    ];

    return (
        <div className="space-y-5" data-testid="contracts-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-display flex items-center gap-2 text-xl font-bold text-slate-800">
                        <ScrollText className="h-5 w-5 text-[#376DB3]" />
                        Contratti
                    </h1>
                    <p className="mt-0.5 text-sm text-slate-500">
                        Stato del contratto di ogni dipendente attivo rispetto all'ultimo UNILAV: genera il contratto direttamente da qui.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2" data-testid="contracts-refresh-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Aggiorna
                </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setFilter(t.key)}
                            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                                filter === t.key ? "bg-[#376DB3] text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}
                            data-testid={`contracts-filter-${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca dipendente o matricola..."
                        className="h-9 w-64 pl-8 text-sm"
                        data-testid="contracts-search-input"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-3 py-2.5">Dipendente</th>
                                <th className="px-3 py-2.5">Matricola</th>
                                <th className="px-3 py-2.5">Stato contratto</th>
                                <th className="px-3 py-2.5">Ultimo UNILAV</th>
                                <th className="px-3 py-2.5">Decorrenza UNILAV</th>
                                <th className="px-3 py-2.5">Ultimo contratto</th>
                                <th className="px-3 py-2.5">Livello / Ore</th>
                                <th className="px-3 py-2.5 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-400" data-testid="contracts-empty-state">
                                        Nessun dipendente in questa vista.
                                    </td>
                                </tr>
                            )}
                            {filtered.map(({ employee: emp, unilav: u, status, last_contract_at }) => (
                                <tr key={emp.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50/60" data-testid={`contract-row-${emp.id}`}>
                                    <td className="px-3 py-2 font-medium text-slate-800">
                                        <Link to={`/dipendenti/${emp.id}`} className="hover:text-[#376DB3] hover:underline">
                                            {emp.cognome} {emp.nome}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-2 font-mono-numbers text-slate-600">{emp.matricola || "—"}</td>
                                    <td className="px-3 py-2">
                                        <Badge variant="outline" className={`text-xs ${STATUS_META[status].cls}`} data-testid={`contract-status-${emp.id}`}>
                                            {STATUS_META[status].label}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600">
                                        {u ? (TIPO_LABEL[u.tipo_comunicazione] || u.tipo_comunicazione) : "—"}
                                        {u?.tipo_contratto ? <span className="block text-[10px] text-slate-400">{u.tipo_contratto}</span> : null}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtIt(u?.data_inizio)}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-600" title={last_contract_at ? "Data dell'ultimo contratto in archivio (decorrenza se generato dal portale, altrimenti data di caricamento)" : "Nessun contratto in archivio"}>
                                        {fmtIt(last_contract_at)}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                                        {u?.livello_inquadramento ? `${u.livello_inquadramento}°` : "—"} / {u?.ore_settimanali ? `${u.ore_settimanali}h` : "—"}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex justify-end gap-1.5">
                                            {(() => {
                                                const letterKind = u?.tipo_comunicazione === "trasformazione" ? "trasformazione" : "proroga";
                                                return (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setLetterTarget({ employee: emp, kind: letterKind })}
                                                        className={`gap-1.5 ${u?.tipo_comunicazione === letterKind ? "border-sky-300 text-sky-800 hover:bg-sky-50" : ""}`}
                                                        title={`Genera la comunicazione di ${letterKind} al dipendente (dati dall'UNILAV)`}
                                                        data-testid={`letter-row-btn-${emp.id}`}
                                                    >
                                                        <Mail className="h-3.5 w-3.5" />
                                                        {letterKind === "trasformazione" ? "Com. trasformazione" : "Com. proroga"}
                                                    </Button>
                                                );
                                            })()}
                                            <Button
                                                size="sm"
                                                variant={status === "ok" ? "outline" : "default"}
                                                onClick={() => setGenEmployee(emp)}
                                                className={`gap-1.5 ${status === "ok" ? "" : "bg-[#376DB3] text-white hover:bg-[#376DB3]/90"}`}
                                                data-testid={`generate-contract-row-btn-${emp.id}`}
                                            >
                                                <FileSignature className="h-3.5 w-3.5" />
                                                Genera contratto
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <GenerateContractDialog
                open={!!genEmployee}
                onOpenChange={(v) => { if (!v) setGenEmployee(null); }}
                employee={genEmployee}
                onSaved={() => { setGenEmployee(null); load(); }}
            />
            <ProrogaLetterDialog
                open={!!letterTarget}
                onOpenChange={(v) => { if (!v) setLetterTarget(null); }}
                employeeId={letterTarget?.employee?.id}
                kind={letterTarget?.kind || "proroga"}
                onDone={() => { setLetterTarget(null); load(); }}
            />
        </div>
    );
}
